/*
 * Explicit-logout recovery suppression (approved product decision, Phase 8C.1
 * Option 2).
 *
 * A successful server logout of a user must never be undone by a later launch
 * automatically recovering that user's local Workout data, even when the
 * best-effort cleanup that follows it (purge, owner-hint clear) fails. The ids
 * of users who explicitly logged out here are kept until that same user
 * authenticates again; while the owner hint names one of them,
 * `probeLocalRecovery` recovers nothing.
 *
 * Boundaries:
 * - It only stops *automatic local recovery*. It never deletes data, never
 *   blocks or authorizes signing in, and never replaces AuthBootstrapProvider.
 * - It lives in localStorage on purpose: a failure domain independent from the
 *   IndexedDB writes it backs up, so one IndexedDB failure cannot lose both.
 *   No IndexedDB store or version change, no backend state.
 * - It holds opaque user ids only: no Workout content, token or auth material.
 * - A set, not a single id: two tabs can each complete a logout (for example a
 *   stale tab of A after B logged out with a failed hint clear), and a single
 *   value would let A's marker overwrite B's while the hint still names B.
 * - Storage failures never throw. A failed write returns false (the caller
 *   logs it; the logout still completes). An unreadable marker reads as "not
 *   suppressed": storage that cannot be read could not hold a marker either,
 *   and Session-expiry recovery must keep working.
 */
const LOCAL_RECOVERY_SUPPRESSION_KEY = "lz_workout_recovery_suppressed";
// Distinct users who logged out here and never signed in again since. Only
// the user the owner hint names matters, and every newer sign-in rewrites the
// hint, so the oldest entries beyond this bound are never the hinted user.
const MAX_SUPPRESSED_USERS = 20;

export function defaultRecoveryStorage() {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch (_error) {
    return null;
  }
}

// A corrupt value reads as empty rather than blocking a new suppression.
function readSuppressedIds(storage) {
  let parsed;
  try {
    parsed = JSON.parse(storage.getItem(LOCAL_RECOVERY_SUPPRESSION_KEY) || "[]");
  } catch (_error) {
    return [];
  }
  return Array.isArray(parsed)
    ? parsed.filter((id) => typeof id === "string" && id)
    : [];
}

/** After a successful server logout only. Returns whether it was persisted. */
export function suppressLocalRecovery(userId, storage = defaultRecoveryStorage()) {
  if (!userId || !storage) return false;
  try {
    const ids = readSuppressedIds(storage).filter((id) => id !== userId);
    ids.push(userId);
    storage.setItem(
      LOCAL_RECOVERY_SUPPRESSION_KEY,
      JSON.stringify(ids.slice(-MAX_SUPPRESSED_USERS))
    );
    return true;
  } catch (_error) {
    return false;
  }
}

/** On that same user's authenticated bootstrap. Never touches another user. */
export function releaseLocalRecoverySuppression(userId, storage = defaultRecoveryStorage()) {
  if (!userId || !storage) return false;
  try {
    const ids = readSuppressedIds(storage);
    if (!ids.includes(userId)) return false;
    const remaining = ids.filter((id) => id !== userId);
    if (remaining.length) {
      storage.setItem(LOCAL_RECOVERY_SUPPRESSION_KEY, JSON.stringify(remaining));
    } else {
      storage.removeItem(LOCAL_RECOVERY_SUPPRESSION_KEY);
    }
    return true;
  } catch (_error) {
    return false;
  }
}

export function isLocalRecoverySuppressed(userId, storage = defaultRecoveryStorage()) {
  if (!userId || !storage) return false;
  try {
    return readSuppressedIds(storage).includes(userId);
  } catch (_error) {
    return false;
  }
}

export { LOCAL_RECOVERY_SUPPRESSION_KEY, MAX_SUPPRESSED_USERS };
